import React, { useRef, useEffect } from "react";
import Modal from "../../shared/Modal";
import { useDemo } from "../../../contexts/DemoContext";
import { MODAL_TYPES } from "../../../demo/annualReviewDemoLogic";

const formatPhoneNumber = (phoneNumber) => {
  // Remove all non-numeric characters
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  
  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  
  return phoneNumber;
};

const CallConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  client,
  callNowButtonRef: externalRef,
}) => {
  const { onAction, isDemoMode } = useDemo();
  
  // Always call hooks first!
  const internalRef = useRef(null);
  useEffect(() => {
    if (externalRef && internalRef.current) {
      externalRef.current = internalRef.current;
    }
  }, [externalRef]);

  // Add spotlight effect for demo mode
  useEffect(() => {
    if (!isDemoMode || !isOpen || !internalRef.current) return;

    const event = new CustomEvent("spotlight", {
      detail: {
        target: internalRef.current,
        label: "Click 'Call Now' to begin the meeting",
        disableAutoAdvance: true,
      },
    });

    setTimeout(() => {
      window.dispatchEvent(event);
    }, 300); // Delay to ensure the modal animation has finished
  }, [isOpen, isDemoMode]);

  if (!client) return null;

  const handleConfirm = () => {
    console.log('🎯 Call confirmed, dispatching CALL_CONFIRMED then AI_SIDEBAR_INTRODUCTION');
    if (isDemoMode) {
      onClose?.(); // Close this modal first
      onAction({ type: 'CALL_CONFIRMED' });          // <-- critical
      onAction({
        type: MODAL_TYPES.AI_SIDEBAR_INTRODUCTION
      });
    } else {
      onConfirm?.();
      onClose?.();
    }
  };

  if (!isOpen) return null;

  // Use demo data if in demo mode and no client provided
  const demoClient = {
    firstName: "Thomas",
    lastName: "Borer",
    contact: {
      primary_number: "(979) 931-6078",
      phoneType: "Mobile"
    }
  };

  const effectiveClient = isDemoMode ? (client || demoClient) : client;
  if (!effectiveClient) return null;

  // Check in multiple possible locations for phone number
  const phoneNumber =
    effectiveClient?.contact?.primary_number ||
    effectiveClient?.contact?.phone ||
    effectiveClient?.primary_number ||
    effectiveClient?.phone ||
    effectiveClient?.ContactInformation?.primary_number ||
    "No phone number";

  const phoneType =
    effectiveClient?.contact?.phoneType ||
    effectiveClient?.contact?.primary_number_type ||
    effectiveClient?.phoneType ||
    effectiveClient?.ContactInformation?.primary_number_type;
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="x-small"     // Use standardized small size for confirmations
      maxHeight="default"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-[clamp(0.5rem,1vw,0.75rem)]">
        <div className="text-center">
          <div className="mb-[clamp(0.5rem,1vw,0.75rem)]">
            <div className="mx-auto flex items-center justify-center h-[clamp(1.5rem,3vw,2rem)] w-[clamp(1.5rem,3vw,2rem)] rounded-full bg-green-100 dark:bg-green-900">
              <svg
                className="h-[clamp(1rem,2vw,1.25rem)] w-[clamp(1rem,2vw,1.25rem)] text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
          </div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-[clamp(0.25rem,0.5vw,0.5rem)] text-[clamp(0.875rem,1.5vw,1.25rem)]">
            Call {effectiveClient.firstName} {effectiveClient.lastName}?
          </h3>
          <p className="text-[clamp(0.75rem,1vw,0.875rem)] text-gray-600 dark:text-gray-400 mb-[clamp(0.5rem,1vw,0.75rem)]">
            {formatPhoneNumber(phoneNumber)} {phoneType && `(${phoneType})`}
          </p>
        </div>
        <div className="flex justify-center space-x-[clamp(0.25rem,0.5vw,0.5rem)]">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-[clamp(0.5rem,1vw,0.75rem)] py-[clamp(0.25rem,0.5vw,0.5rem)] text-[clamp(0.75rem,1vw,0.875rem)] transition-colors hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            id="call-now-btn"
            ref={internalRef}
            data-tour="call-button"
            onClick={handleConfirm}
            className="rounded-lg bg-green-600 dark:bg-green-500 text-white px-[clamp(0.5rem,1vw,0.75rem)] py-[clamp(0.25rem,0.5vw,0.5rem)] text-[clamp(0.75rem,1vw,0.875rem)] transition-colors hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            Call Now
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CallConfirmationModal;
